// hardcoded-ui-text: allow — the sidebar's visible labels go through t(). The `description`
// field on each item is never rendered: nothing in this file or anywhere else reads it.
// Left in place rather than deleted — removing code is not translating it — but flagged here
// so no one translates a string no person will ever see.
import { useState } from "react";
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import {
  Home,
  MessageSquare,
  Settings,
  Phone,
  Users,
  CreditCard,
  Building2,
  ChevronRight,
  ChevronDown,
  User,
  Palette,
  Mail,
  Globe,
  AlertTriangle,
  Bot,
  Workflow,
  Brain,
  BookOpen,
  Package,
  Zap,
  LifeBuoy,
  CalendarDays,
  CalendarClock,
  ListChecks,
  Handshake,
  Bell,
  Database,
  Wallet,
  ShoppingBag,
  FileCheck2,
  FileStack,
  KeyRound,
  LayoutGrid,
  TestTube
} from "lucide-react";
import { FaTiktok, FaInstagram, FaFacebook } from 'react-icons/fa';
import { NavLink, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { useChannelManagement } from "@/hooks/useChannelManagement";
import { LanguageSelector } from '@/components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/hooks/usePermissions';
import { STATUS_ICONS } from '@/lib/status-appearance';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const getChannelIcon = (iconName: string) => {
  const icons: Record<string, unknown> = {
    'MessageSquare': MessageSquare,
    'Phone': Phone,
    'Mail': Mail,
    'Globe': Globe,
    'Instagram': FaInstagram,
    'Tiktok': FaTiktok,
    'Facebook': FaFacebook,
  };
  return icons[iconName] || MessageSquare;
};

export function AppSidebar() {
  const { user, userProfile, tenantData, signOut } = useAuth();
  const { signedUrl: signedLogoUrl } = useSignedMediaUrl(tenantData?.logo_url ?? null);
  const { setTheme } = useTheme();
  const { t } = useTranslation('common');
  // get_user_permissions returns text[] (read from the database on 2026-09-20); the generated
  // types describe it as something else, so the array is named for what it is here.
  const { data: permissionsHeld = [] } = usePermissions();
  const permissions = permissionsHeld as unknown as string[];
  const location = useLocation();
  const currentPath = location.pathname;
  const { availableChannels, isLoading: channelsLoading, error: channelsError, refreshChannels } = useChannelManagement();
  // Task 1.14 — App Studio is gated on the current tenant's enabled_modules
  // (tenantData comes from useAuth, loaded for the logged-in tenant_admin's own
  // tenant). Platform admins always pass. The useRuntimeAccess hook is NOT used
  // here: its currentConfig depends on the platform-admin tenant-switcher
  // (selectedTenant), which is null for ordinary tenant_admins.
  const isPlatformAdmin = userProfile?.role === 'platform_admin';
  const appStudioModuleEnabled =
    isPlatformAdmin || (tenantData?.enabled_modules?.includes('app_studio') ?? false);
  const [channelsExpanded, setChannelsExpanded] = useState(true);
  const [conversationsExpanded, setConversationsExpanded] = useState(false);
  // N-06: collapsed by default -- this is the developer's workbench, not the merchant's
  // daily work, and it used to sit expanded at the same weight as "Customers" and "Orders".
  const [advancedToolsExpanded, setAdvancedToolsExpanded] = useState(false);

  const mainNavItems = [
    {
      title: t('dashboard'),
      url: "/dashboard",
      icon: Home,
      description: "Overview and analytics"
    },
    {
      title: t('conversations'),
      url: "/conversations",
      icon: MessageSquare,
      description: "Manage customer chats",
      subItems: [
        {
          title: t('quickReplies'),
          url: "/conversations/quick-replies",
          icon: Zap,
          description: "Manage quick reply templates",
          requiredRole: "tenant_admin"
        },
        {
          title: t('escalationSettings'),
          url: "/conversations/escalations",
          icon: LifeBuoy,
          description: "Configure escalation keywords, messages, and thresholds",
          requiredRole: "tenant_admin"
        },
        {
          title: t('autoReminders'),
          url: "/tenant-admin/templates/whatsapp",
          icon: Bell,
          description: t('autoRemindersDescription'),
          requiredRole: "tenant_admin"
        }
      ]
    },
  ];

  // N-06 / A-27: this used to be one 18-item list mixing daily merchant work with the
  // developer's workbench at the same visual weight, and it also ran off the bottom of a
  // 900px window with the two money screens past the fold. Split in two: the business
  // items the tenant admin actually opens each day (billing and the account itself moved
  // near the top, per A-27), and the advanced/testing tools collapsed by default (N-06),
  // so the always-visible list is short enough to fit without depending on scroll alone.
  const businessAdminItems = [
    {
      title: t('tenantAdmin:myBusinessNav'),
      url: "/tenant-admin",
      icon: Building2,
      description: "Manage users and settings",
      requiredRole: "tenant_admin"
    },
    {
      title: t('billingAndUsage'),
      url: "/billing",
      icon: CreditCard,
      description: "Billing and subscription",
      requiredRole: "tenant_admin"
    },
    {
      title: t('paymentProviders'),
      url: "/tenant-admin/payments",
      icon: Wallet,
      description: "Configure Transbank and Mercado Pago payment integrations",
      requiredRole: "tenant_admin"
    },
    {
      title: t('credentials'),
      url: "/tenant-admin/credentials",
      icon: KeyRound,
      description: "Passwords, tokens and authenticator keys for automations",
      requiredRole: "tenant_admin"
    },
    {
      title: t('userManagement'),
      url: "/tenant-admin/users",
      icon: Users,
      description: "Manage users and permissions",
      requiredRole: "tenant_admin",
      // The PAGE asks for a permission, not a role (PermissionGuard permission="users.view"), so
      // filtering the menu by role alone offered a door the account could not open: it answered
      // «acceso denegado» to the same person the menu had just invited. Measured 2026-09-17.
      requiredPermission: "users.view",
    },
    {
      title: t('productCatalog'),
      url: "/tenant-admin/products",
      icon: Package,
      description: "Manage product inventory and pricing",
      requiredRole: "tenant_admin"
    },
    {
      title: t('customers'),
      url: "/tenant-admin/customers",
      icon: Users,
      description: "View and manage customer contacts"
    },
    {
      title: t('orders'),
      url: "/tenant-admin/orders",
      icon: ShoppingBag,
      description: "View and manage customer orders"
    },
    {
      title: t('documentReview'),
      url: "/tenant-admin/document-review",
      icon: FileCheck2,
      description: "Confirm document fields the model was unsure about"
    },
    {
      title: t('businessHoursNav'),
      url: "/tenant-admin/business-hours",
      icon: CalendarClock,
      description: "Weekly schedule, appointment capacity, and calendar exceptions",
      requiredRole: "tenant_admin"
    },
    {
      title: t('ruleSetsNav'),
      url: "/tenant-admin/rule-sets",
      icon: ListChecks,
      description: "The rule sets the business rules steps read, their versions and the flows that use them",
      requiredRole: "tenant_admin"
    },
    {
      title: t('documentTypes'),
      url: "/tenant-admin/document-types",
      icon: FileStack,
      requiredRole: "tenant_admin",
      description: "Declare the documents this space receives and the fields it needs off each one"
    },
    {
      title: t('partnerDirectory'),
      url: "/partners",
      icon: Handshake,
      description: "Manage B2B partners with custom pricing and routing",
      requiredRole: "tenant_admin"
    },
    // Calendar Booking — gated on the tenant's own module flag, not a hand-picked tenant
    // id (A-36 fix): the route already works for any tenant (App.tsx), the menu entry was
    // the only thing still hard-coded to one customer.
    ...(tenantData?.enabled_modules?.includes('calendar_booking') ? [{
      title: t('calendarBooking'),
      // A-41: the one tenant-admin path written in Spanish while every sibling is English.
      // Left as-is on purpose, since renaming a published path breaks saved bookmarks. The
      // criterion for the next route path that gets decided: pick one convention and keep
      // it, instead of deciding per route.
      url: "/tenant-admin/agendamiento",
      icon: CalendarDays,
      description: "Manage service bookings and appointments",
      requiredRole: "tenant_admin" as const
    }] : []),
    {
      title: t('templates'),
      url: "/tenant-admin/templates",
      icon: Palette,
      description: "Manage email and WhatsApp message templates",
      requiredRole: "tenant_admin"
    },
  ];

  const advancedToolsItems = [
    {
      title: t('aiAgents'),
      url: "/ai-agents",
      icon: Bot,
      description: "Manage AI agents and configurations",
      requiredRole: "tenant_admin"
    },
    {
      title: t('aiWorkflows'),
      url: "/ai-workflows",
      icon: Workflow,
      description: "Build and manage AI workflows",
      requiredRole: "tenant_admin"
    },
    {
      title: t('contextStores'),
      url: "/tenant-admin/context-stores",
      icon: BookOpen,
      description: "Manage AI knowledge bases for enhanced responses",
      requiredRole: "tenant_admin"
    },
    {
      title: t('testingSuite'),
      url: "/tenant-admin/testing",
      icon: TestTube,
      description: t('testingSuiteDescription'),
      requiredRole: "tenant_admin"
    },
    {
      title: t('conversationSimulator'),
      url: "/tenant-admin/conversation-simulator",
      icon: MessageSquare,
      description: t('conversationSimulatorDescription'),
      requiredRole: "tenant_admin"
    },
    {
      title: t('dataAndFunctions'),
      url: "/tenant-admin/custom-extensions",
      icon: Database,
      description: "Custom data tables and functions",
      requiredRole: "tenant_admin"
    },
    // App Studio — alpha, behind VITE_ENABLE_APP_STUDIO global kill-switch AND
    // per-tenant enabled_modules gating (Task 1.14). The env flag is the global
    // on/off; appStudioModuleEnabled scopes it to tenants that have the module.
    ...(import.meta.env.VITE_ENABLE_APP_STUDIO === 'true' && appStudioModuleEnabled ? [{
      title: t('appStudio'),
      url: "/tenant-admin/app-studio",
      icon: LayoutGrid,
      description: "No-code page builder (alpha)",
      requiredRole: "tenant_admin" as const
    }] : []),
    {
      title: t('aiSettings'),
      url: "/ai-settings",
      icon: Brain,
      description: "Configure AI models and settings",
      requiredRole: "tenant_admin",
      // A-51: the page itself already says "Coming Soon" (TenantAISettings.tsx) --
      // this puts the same badge here too, so a merchant knows before clicking in.
      comingSoon: true
    },
  ];

  const isActive = (path: string) => currentPath === path;
  const getNavClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "bg-accent text-accent-foreground font-medium" : "hover:bg-accent/50";

  const hasRequiredRole = (requiredRole?: string) => {
    if (!requiredRole) return true;
    return userProfile?.role === requiredRole || userProfile?.role === 'platform_admin';
  };

  // A menu entry must ask what its destination asks. Where an item names a permission, that is the
  // question; role alone is not enough, and an entry nobody can open is worse than a missing one.
  const filteredAdminItems = businessAdminItems.filter(
    (item) => hasRequiredRole(item.requiredRole)
      && (!('requiredPermission' in item) || !item.requiredPermission
        || permissions.includes(item.requiredPermission as string)),
  );
  const filteredAdvancedItems = advancedToolsItems.filter(item => hasRequiredRole(item.requiredRole));

  // Add coming soon channels (for all tenants) - showing as "connected" for mockup.
  // `status: 'connected'` here is NOT a live read of anything -- these six rows have no
  // account behind them, and clicking any of them lands on `<ComingSoon />` (see App.tsx),
  // which already tells the truth. `isMockup: true` is what keeps the sidebar from
  // contradicting the very page it links to: it swaps the green "connected" tick for the
  // same "coming soon" wording that page already uses, instead of removing the row --
  // whether to preview these channels at all is a product decision, not this fix's to make.
  const comingSoonChannels = [
    {
      name: 'instagram',
      display_name: t('instagram'),
      icon: 'Instagram',
      is_configured: true,
      is_enabled: true,
      status: 'connected' as const,
      isMockup: true as const,
    },
    {
      name: 'tiktok',
      display_name: t('tiktok'),
      icon: 'Tiktok',
      is_configured: true,
      is_enabled: true,
      status: 'connected' as const,
      isMockup: true as const,
    },
    {
      name: 'facebook',
      display_name: t('facebook'),
      icon: 'Facebook',
      is_configured: true,
      is_enabled: true,
      status: 'connected' as const,
      isMockup: true as const,
    },
    {
      name: 'phone',
      display_name: t('phone'),
      icon: 'Phone',
      is_configured: true,
      is_enabled: true,
      status: 'connected' as const,
      isMockup: true as const,
    },
    {
      name: 'sms',
      display_name: t('sms'),
      icon: 'Phone',
      is_configured: true,
      is_enabled: true,
      status: 'connected' as const,
      isMockup: true as const,
    },
    {
      name: 'email',
      display_name: t('email'),
      icon: 'Mail',
      is_configured: true,
      is_enabled: true,
      status: 'connected' as const,
      isMockup: true as const,
    },
  ];

  // Filter channels - show configured or setup_required channels for tenant admins
  const availableChannelsForUser = hasRequiredRole("tenant_admin")
    ? [...availableChannels.filter(channel => channel.is_configured || channel.status === 'setup_required'), ...comingSoonChannels]
    : [];

  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getBusinessInitials = (businessName?: string) => {
    // A-55: the old fallback ("WA", for the product's discontinued "WhatsApp CRM" name) put a
    // stale brand in front of every new tenant before they had entered a business name.
    if (!businessName) return "OK";
    return businessName
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Not migrated to status-appearance.ts on purpose: this is the person's role, not an
  // entity's status — a different axis the module doesn't cover.
  const getRoleBadgeVariant = (role?: string) => {
    switch (role) {
      case 'tenant_admin': return 'default';
      case 'supervisor': return 'secondary';
      case 'agent': return 'outline';
      default: return 'outline';
    }
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-border">
        <div className="flex items-center gap-2 px-4 py-3 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:justify-center">
          <Avatar className="h-8 w-8 rounded-lg flex-shrink-0">
            <AvatarImage src={signedLogoUrl || ''} alt={tenantData?.business_name || t('businessLogoAlt')} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold rounded-lg">
              {getBusinessInitials(tenantData?.business_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
            {tenantData?.business_name ? (
              <h2 className="font-semibold text-foreground truncate">
                {tenantData.business_name}
              </h2>
            ) : (
              // A-55: the fallback used to be "WhatsApp CRM", the product's discontinued
              // name -- the first thing a new tenant saw in their own space. An empty
              // business name is this tenant's own gap to close, not the product's name
              // to fall back on, so it links to where it gets completed.
              <NavLink
                to="/tenant-admin"
                className="text-sm font-medium text-muted-foreground hover:text-foreground truncate block"
              >
                {t('addBusinessName')}
              </NavLink>
            )}
          </div>
          <SidebarTrigger className="group-data-[collapsible=icon]:hidden" />
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>{t('main')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <div key={item.title}>
                  <SidebarMenuItem>
                    <div className="flex items-center">
                      <SidebarMenuButton asChild tooltip={item.title} className="flex-1">
                        <NavLink to={item.url} end className={getNavClass}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                      {item.subItems && item.subItems.length > 0 && hasRequiredRole(item.subItems[0].requiredRole) && (
                        <button
                          onClick={() => {
                            if (item.url === "/conversations") {
                              setConversationsExpanded(!conversationsExpanded);
                            }
                          }}
                          className="p-1.5 hover:bg-accent rounded-md transition-colors"
                          aria-label={t(
                            (item.url === "/conversations" && conversationsExpanded)
                              ? 'sidebar.collapseSection' : 'sidebar.expandSection',
                            { section: item.title },
                          )}
                          aria-expanded={item.url === "/conversations" ? conversationsExpanded : undefined}
                        >
                          {(item.url === "/conversations" && conversationsExpanded) ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      )}
                    </div>
                  </SidebarMenuItem>

                  {/* Sub-items */}
                  {item.subItems && item.url === "/conversations" && conversationsExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.subItems
                        .filter(subItem => hasRequiredRole(subItem.requiredRole))
                        .map((subItem) => (
                          <SidebarMenuItem key={subItem.title}>
                            <SidebarMenuButton asChild tooltip={subItem.title} size="sm">
                              <NavLink to={subItem.url} end className={getNavClass}>
                                <subItem.icon className="h-3.5 w-3.5" />
                                <span className="text-sm">{subItem.title}</span>
                              </NavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin Navigation - Show only if user has relevant role */}
        {filteredAdminItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('administration')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {filteredAdminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <NavLink to={item.url} end className={getNavClass}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Advanced tools - collapsed by default (N-06): testing, AI configuration and the
            data workbench, separated from the merchant's daily work above. */}
        {filteredAdvancedItems.length > 0 && (
          <SidebarGroup>
            <button
              onClick={() => setAdvancedToolsExpanded(!advancedToolsExpanded)}
              className="w-full flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              {advancedToolsExpanded ? (
                <ChevronDown className="h-4 w-4 text-sidebar-foreground/70" />
              ) : (
                <ChevronRight className="h-4 w-4 text-sidebar-foreground/70" />
              )}
              <SidebarGroupLabel className="flex-1">{t('tenantAdmin:advancedTools')}</SidebarGroupLabel>
            </button>
            {advancedToolsExpanded && (
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredAdvancedItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={'comingSoon' in item && item.comingSoon ? `${item.title} (${t('comingSoon')})` : item.title}
                      >
                        <NavLink to={item.url} end className={getNavClass}>
                          <item.icon className="h-4 w-4" />
                          <div className="flex items-center justify-between flex-1 min-w-0">
                            <span>{item.title}</span>
                            {'comingSoon' in item && item.comingSoon && (
                              <Badge
                                variant="outline"
                                className="h-4 px-1 text-[9px] leading-none font-normal flex-shrink-0 text-muted-foreground"
                              >
                                {t('comingSoon')}
                              </Badge>
                            )}
                          </div>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {/* Channels Section - Show configured channels for tenant admins */}
        {(channelsLoading || channelsError || availableChannelsForUser.length > 0) && (
          <SidebarGroup>
            <button
              onClick={() => setChannelsExpanded(!channelsExpanded)}
              className="w-full flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              {channelsExpanded ? (
                <ChevronDown className="h-4 w-4 text-sidebar-foreground/70" />
              ) : (
                <ChevronRight className="h-4 w-4 text-sidebar-foreground/70" />
              )}
              <SidebarGroupLabel className="flex-1">{t('channels')}</SidebarGroupLabel>
            </button>
            {channelsExpanded && (
              <SidebarGroupContent>
                <SidebarMenu>
                  {channelsLoading ? (
                    // H-28: this used to be the only branch, with no upper bound -- a
                    // hung request in useChannelManagement/useRuntimeAccess left this
                    // pulsing block on screen forever, in the first place the operator's
                    // eye lands. Both hooks now carry a 15s watchdog that turns a stuck
                    // load into `channelsError`, so this branch is bounded and the error
                    // branch below is what actually renders once it fires.
                    <SidebarMenuItem>
                      <div className="px-2 py-1.5">
                        <div className="h-8 bg-muted/50 rounded animate-pulse"></div>
                      </div>
                    </SidebarMenuItem>
                  ) : channelsError ? (
                    <SidebarMenuItem>
                      <div className="px-2 py-1.5 flex flex-col gap-1.5">
                        <span className="text-xs text-destructive">{t('channelManagement.loadFailed')}</span>
                        <button
                          onClick={() => refreshChannels()}
                          className="text-xs font-medium text-left text-primary hover:underline w-fit"
                        >
                          {t('retry')}
                        </button>
                      </div>
                    </SidebarMenuItem>
                  ) : availableChannelsForUser.length === 0 ? (
                    <SidebarMenuItem>
                      <div className="px-2 py-1.5">
                        <span className="text-xs text-muted-foreground">{t('channelManagement.empty')}</span>
                      </div>
                    </SidebarMenuItem>
                  ) : (
                    availableChannelsForUser.map((channel) => {
                      const IconComponent = getChannelIcon(channel.icon);
                      // No verified icon falls through to AlertTriangle, same as the local
                      // function this replaces did for its `default` case.
                      const StatusIcon = STATUS_ICONS[channel.status] ?? AlertTriangle;
                      const channelUrl = `/${channel.name}-admin`;
                      // `isMockup` only exists on `comingSoonChannels` -- `'isMockup' in channel`
                      // keeps this safe for the real, hook-sourced channels that don't carry it.
                      const isMockup = 'isMockup' in channel && channel.isMockup === true;

                      return (
                        <SidebarMenuItem key={channel.name}>
                          <SidebarMenuButton
                            asChild
                            tooltip={isMockup ? `${channel.display_name} (${t('comingSoon')})` : channel.display_name}
                          >
                            <NavLink to={channelUrl} end className={getNavClass}>
                              <IconComponent className="h-4 w-4" />
                              <div className="flex items-center justify-between flex-1 min-w-0">
                                <span>{channel.display_name}</span>
                                {isMockup ? (
                                  <Badge
                                    variant="outline"
                                    className="h-4 px-1 text-[9px] leading-none font-normal flex-shrink-0 text-muted-foreground"
                                  >
                                    {t('comingSoon')}
                                  </Badge>
                                ) : (
                                  <StatusIcon className={`h-3 w-3 flex-shrink-0 ${
                                    channel.status === 'connected' ? 'text-green-500' :
                                    channel.status === 'setup_required' ? 'text-yellow-500' :
                                    'text-red-500'
                                  }`} />
                                )}
                              </div>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {/* Preferences */}
        <SidebarGroup>
          <SidebarGroupLabel>{t('preferences')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton tooltip={t('theme')}>
                      <Palette className="h-4 w-4" />
                      <span>{t('theme')}</span>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setTheme("light")}>
                      {t('light')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme("dark")}>
                      {t('dark')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme("system")}>
                      {t('system')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <div className="px-2 py-1.5">
                  <LanguageSelector />
                </div>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src="" />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs rounded-lg">
                      {getUserInitials(userProfile?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {userProfile?.full_name || user?.email}
                    </span>
                    {userProfile?.role && (
                      <span className="truncate text-xs text-muted-foreground">
                        {userProfile.role.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 rounded-lg" side="bottom" align="end" sideOffset={4}>
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src="" />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs rounded-lg">
                        {getUserInitials(userProfile?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {userProfile?.full_name || user?.email}
                      </span>
                      {userProfile?.role && (
                        <Badge variant={getRoleBadgeVariant(userProfile.role)} className="text-xs w-fit">
                          {userProfile.role.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <NavLink to="/settings" className="flex items-center">
                    <Settings className="mr-2 h-4 w-4" />
                    {t('settings')}
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive">
                  {t('signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}